import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea.tsx';
import { Label } from '@/components/ui/label.tsx';
import type { WebsiteSchema, WebsiteField, WebsiteFieldCategory, WebsiteExtractionResult, ConfidenceLevel } from '@/types/blueprint.ts';
import { CategoryScreenshots } from './CategoryScreenshots.tsx';
import SingleFieldEditor from './SingleFieldEditor.tsx';
import FinishActions from './FinishActions.tsx';
import HeaderSection from './HeaderSection.tsx';
import { toast } from 'sonner';
import { websiteExtractionResult } from './sampleData.ts';

interface WebsiteSchemaEditorProps {
  websiteSchema: WebsiteSchema;
}

enum FieldStatus {
  Pending = "pending",
  Completed = "completed",
  NeedUserReview = "need_user_review"
}

enum CategoryStatus {
  Pending = "pending", 
  Completed = "completed",
  NeedUserReview = "need_user_review"
}

type AnswersState = Record<string, Record<string, string>>;
type NotesState = Record<string, Record<string, string>>;
type FieldStatusState = Record<string, Record<string, FieldStatus>>;
type CategoryStatusState = Record<string, CategoryStatus>;
type CategoryInstructionsState = Record<string, string>;

type CategoryWithScreenshots = WebsiteFieldCategory & { screenshots?: import('./CategoryScreenshots').Screenshot[] };

const WebsiteSchemaEditor: React.FC<WebsiteSchemaEditorProps> = ({ websiteSchema }) => {
  // Initialize data from sample data and determine field availability
  const initializeFromSampleData = () => {
    const initialAnswers: AnswersState = {};
    const initialNotes: NotesState = {};
    const initialFieldStatus: FieldStatusState = {};
    const initialCategoryStatus: CategoryStatusState = {};
    const initialReasoning: Record<string, Record<string, string>> = {};
    const fieldAvailability: Record<string, Record<string, boolean>> = {};

    // First, mark all schema fields as unavailable by default
    websiteSchema.categories.forEach(schemaCategory => {
      const categoryId = schemaCategory.category_id;
      initialAnswers[categoryId] = {};
      initialNotes[categoryId] = {};
      initialFieldStatus[categoryId] = {};
      initialReasoning[categoryId] = {};
      fieldAvailability[categoryId] = {};
      
      // Mark all schema fields as unavailable initially
      schemaCategory.fields.forEach(schemaField => {
        fieldAvailability[categoryId][schemaField.field_id] = false;
        // Set default status for unavailable fields
        initialFieldStatus[categoryId][schemaField.field_id] = FieldStatus.Pending;
      });
    });

    // Then, update with available data from extraction result
    websiteExtractionResult.categories.forEach(categoryResult => {
      const categoryId = categoryResult.category_id;
      
      categoryResult.field_answers.forEach(fieldAnswer => {
        // Mark this field as available
        if (fieldAvailability[categoryId]) {
          fieldAvailability[categoryId][fieldAnswer.field_id] = true;
        }
        
        initialAnswers[categoryId][fieldAnswer.field_id] = fieldAnswer.answer;
        initialNotes[categoryId][fieldAnswer.field_id] = ''; // Keep notes empty by default
        initialReasoning[categoryId][fieldAnswer.field_id] = fieldAnswer.reasoning || '';
        
        // Set field status based on confidence level for available fields
        if (fieldAnswer.confidence === 'missing' as ConfidenceLevel) {
          initialFieldStatus[categoryId][fieldAnswer.field_id] = FieldStatus.NeedUserReview;
        } else if (fieldAnswer.confidence === 'low' as ConfidenceLevel) {
          initialFieldStatus[categoryId][fieldAnswer.field_id] = FieldStatus.NeedUserReview;
        } else {
          initialFieldStatus[categoryId][fieldAnswer.field_id] = FieldStatus.Completed;
        }
      });

      // Determine category status based on field statuses (only for available fields)
      const availableFieldStatuses = Object.keys(fieldAvailability[categoryId] || {})
        .filter(fieldId => fieldAvailability[categoryId][fieldId])
        .map(fieldId => initialFieldStatus[categoryId][fieldId]);
        
      if (availableFieldStatuses.some(status => status === FieldStatus.NeedUserReview)) {
        initialCategoryStatus[categoryId] = CategoryStatus.NeedUserReview;
      } else if (availableFieldStatuses.every(status => status === FieldStatus.Completed)) {
        initialCategoryStatus[categoryId] = CategoryStatus.Completed;
      } else {
        initialCategoryStatus[categoryId] = CategoryStatus.Pending;
      }
    });

    return { initialAnswers, initialNotes, initialFieldStatus, initialCategoryStatus, initialReasoning, fieldAvailability };
  };

  const { initialAnswers, initialNotes, initialFieldStatus, initialCategoryStatus, initialReasoning, fieldAvailability } = initializeFromSampleData();

  const [result, setResult] = useState<WebsiteExtractionResult>(websiteExtractionResult);

  const generateAnswersText = (
    categories: WebsiteFieldCategory[],
    answers: AnswersState
  ): string => {
    return categories
      .map((cat: WebsiteFieldCategory) =>
        cat.fields
          .map((field: WebsiteField) => {
            const value: string = answers[cat.category_id]?.[field.field_id] ?? '';
            return `${field.field_name}: ${value}`;
          })
          .join('\n')
      )
      .join('\n\n');
  };

  const handleCopyAnswers = (): void => {
    const result: string = generateAnswersText(websiteSchema.categories, answers);
    void navigator.clipboard.writeText(result.trim());
    toast.success('Answers copied to clipboard');
  };

  const handleDownload = (): void => {
    const result: string = generateAnswersText(websiteSchema.categories, answers);
    const blob: Blob = new Blob([result], { type: 'text/plain' });
    const url: string = URL.createObjectURL(blob);
    const a: HTMLAnchorElement = document.createElement('a');
    a.href = url;
    a.download = `${websiteSchema.website_name}-${websiteSchema.insurance_line}-answers.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Answers downloaded');
  };

  const [answers, setAnswers] = useState<AnswersState>(initialAnswers);
  const [notes, setNotes] = useState<NotesState>(initialNotes);
  const [fieldStatuses, setFieldStatuses] = useState<FieldStatusState>(initialFieldStatus);
  const [categoryStatuses, setCategoryStatuses] = useState<CategoryStatusState>(initialCategoryStatus);
  const [categoryInstructions, setCategoryInstructions] = useState<CategoryInstructionsState>({});
  const [reasoning] = useState<Record<string, Record<string, string>>>(initialReasoning);
  const [isFieldAvailable, setIsFieldAvailable] = useState<Record<string, Record<string, boolean>>>(fieldAvailability);
  const [currentCategoryIdx, setCurrentCategoryIdx] = useState<number>(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const categories = websiteSchema.categories;
  const totalCategories = categories.length;
  const currentCategory = categories[currentCategoryIdx];
  const currentCategoryWithScreenshots = currentCategory as CategoryWithScreenshots;
  const fields = currentCategory.fields;

  // Helper function to get category status color and text
  const getCategoryStatusColor = (status: CategoryStatus) => {
    switch (status) {
      case CategoryStatus.Completed:
        return 'bg-green-100 text-green-700';
      case CategoryStatus.NeedUserReview:
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getCategoryStatusText = (status: CategoryStatus) => {
    switch (status) {
      case CategoryStatus.Completed:
        return 'Completed';
      case CategoryStatus.NeedUserReview:
        return 'Needs Review';
      default:
        return 'Pending';
    }
  };

  // Calculate progress
  const totalFields = categories.reduce((acc, cat) => acc + cat.fields.length, 0);
  const answeredFields = Object.values(answers).reduce(
    (acc, catAnswers) => acc + Object.values(catAnswers).filter((v) => v && v.length > 0).length,
    0
  );
  const progressPercent = totalFields === 0 ? 0 : Math.round((answeredFields / totalFields) * 100);
  const fieldsCompleted = Object.values(answers[currentCategory.category_id] || {}).filter((v) => v && v.length > 0).length;

  const handleFieldChange = ({ categoryId, fieldId, value }: { categoryId: string; fieldId: string; value: string }) => {
    setAnswers((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [fieldId]: value,
      },
    }));
    
    // Check if field should become available due to user interaction
    setTimeout(() => checkAndUpdateFieldAvailability(categoryId, fieldId), 0);
  };

  const handleNotesChange = ({ categoryId, fieldId, notes }: { categoryId: string; fieldId: string; notes: string }) => {
    setNotes((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [fieldId]: notes,
      },
    }));
    
    // Check if field should become available due to user interaction
    setTimeout(() => checkAndUpdateFieldAvailability(categoryId, fieldId), 0);
  };

  const handleFieldStatusChange = ({ categoryId, fieldId, status }: { categoryId: string; fieldId: string; status: FieldStatus }) => {
    setFieldStatuses((prev) => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        [fieldId]: status,
      },
    }));
    
    // Check if field should become available due to user interaction
    setTimeout(() => checkAndUpdateFieldAvailability(categoryId, fieldId), 0);
    
    // Update category status after field status changes
    setTimeout(() => updateCategoryStatus(categoryId), 0);
  };

  // Track unsaved changes
  useEffect(() => {
    const answersChanged = JSON.stringify(answers) !== JSON.stringify(initialAnswers);
    const notesChanged = JSON.stringify(notes) !== JSON.stringify(initialNotes);
    const fieldStatusChanged = JSON.stringify(fieldStatuses) !== JSON.stringify(initialFieldStatus);
    const categoryStatusChanged = JSON.stringify(categoryStatuses) !== JSON.stringify(initialCategoryStatus);
    setHasUnsavedChanges(answersChanged || notesChanged || fieldStatusChanged || categoryStatusChanged);
  }, [answers, notes, fieldStatuses, categoryStatuses, initialAnswers, initialNotes, initialFieldStatus, initialCategoryStatus]);

  // Regenerate category instructions when category changes or when notes/statuses change
  useEffect(() => {
    if (currentCategory) {
      generateCategoryInstructions(currentCategory.category_id);
    }
  }, [currentCategoryIdx, notes, fieldStatuses]);

  // Update category status based on field statuses
  const updateCategoryStatus = (categoryId: string) => {
    const categoryFieldStatuses = Object.values(fieldStatuses[categoryId] || {});
    let newStatus: CategoryStatus;
    
    if (categoryFieldStatuses.some(status => status === FieldStatus.NeedUserReview)) {
      newStatus = CategoryStatus.NeedUserReview;
    } else if (categoryFieldStatuses.every(status => status === FieldStatus.Completed)) {
      newStatus = CategoryStatus.Completed;
    } else {
      newStatus = CategoryStatus.Pending;
    }
    
    setCategoryStatuses(prev => ({
      ...prev,
      [categoryId]: newStatus
    }));
  };

  // Check if field has user interaction and mark as available
  const checkAndUpdateFieldAvailability = (categoryId: string, fieldId: string) => {
    const hasValue = answers[categoryId]?.[fieldId] && answers[categoryId][fieldId].trim() !== '';
    const hasNotes = notes[categoryId]?.[fieldId] && notes[categoryId][fieldId].trim() !== '';
    const currentStatus = fieldStatuses[categoryId]?.[fieldId];
    const hasStatusChange = currentStatus !== FieldStatus.Pending;
    const isCompleted = currentStatus === FieldStatus.Completed;
    
    // If user has interacted with the field or marked it as completed, mark it as available
    if (hasValue || hasNotes || hasStatusChange || isCompleted) {
      setIsFieldAvailable(prev => ({
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          [fieldId]: true
        }
      }));
    }
  };

  // Generate category instructions from completed fields
  const generateCategoryInstructions = (categoryId: string) => {
    const category = websiteSchema.categories.find(cat => cat.category_id === categoryId);
    if (!category) return;

    const completedFieldInstructions: string[] = [];
    
    category.fields.forEach(field => {
      const fieldStatus = fieldStatuses[categoryId]?.[field.field_id];
      const fieldNotes = notes[categoryId]?.[field.field_id];
      
      // Include all completed fields, with or without notes
      if (fieldStatus === FieldStatus.Completed) {
        if (fieldNotes && fieldNotes.trim() !== '') {
          // Field has custom notes
          completedFieldInstructions.push(`${field.field_name}: ${fieldNotes.trim()}`);
        } else {
          // Field is completed but no custom notes - add default instruction line
          completedFieldInstructions.push(`${field.field_name}: Field completed and verified`);
        }
      }
    });

    const instructionText = completedFieldInstructions.length > 0 
      ? completedFieldInstructions.join('\n\n') 
      : '';

    setCategoryInstructions(prev => ({
      ...prev,
      [categoryId]: instructionText
    }));
  };

  const handleRefresh = () => {
    if (hasUnsavedChanges) {
      const shouldRefresh = window.confirm(
        'You have unsaved changes. Are you sure you want to refresh? All unsaved data will be lost.'
      );
      if (!shouldRefresh) {
        return;
      }
    }
    
    // Reset all state to initial values
    setAnswers(initialAnswers);
    setNotes(initialNotes);
    setFieldStatuses(initialFieldStatus);
    setCategoryStatuses(initialCategoryStatus);
    setCurrentCategoryIdx(0);
    setHasUnsavedChanges(false);
    toast.info('Refreshed');
  };

  const saveToResult = () => {
    // Update the result with current answers and notes, including new fields added by user
    const updatedCategories = result.categories.map(categoryResult => {
      const categoryId = categoryResult.category_id;
      
      // Update existing field answers
      const updatedFieldAnswers = categoryResult.field_answers.map(fieldAnswer => {
        const fieldId = fieldAnswer.field_id;
        const currentAnswer = answers[categoryId]?.[fieldId];
        const currentNotes = notes[categoryId]?.[fieldId];
        
        if (currentAnswer !== undefined) {
          return {
            ...fieldAnswer,
            answer: currentAnswer,
            reasoning: currentNotes || fieldAnswer.reasoning
          };
        }
        return fieldAnswer;
      });

      // Add new field answers for fields that user interacted with but weren't in original result
      const existingFieldIds = new Set(categoryResult.field_answers.map(fa => fa.field_id));
      const schemaCategory = websiteSchema.categories.find(cat => cat.category_id === categoryId);
      
      if (schemaCategory) {
        schemaCategory.fields.forEach(schemaField => {
          const fieldId = schemaField.field_id;
          const isFieldNowAvailable = isFieldAvailable[categoryId]?.[fieldId];
          const currentAnswer = answers[categoryId]?.[fieldId];
          const currentNotes = notes[categoryId]?.[fieldId];
          
          // If field is now available due to user interaction but wasn't in original result
          if (isFieldNowAvailable && !existingFieldIds.has(fieldId)) {
            updatedFieldAnswers.push({
              answer: currentAnswer || '',
              confidence: 'high' as ConfidenceLevel,
              field_id: fieldId,
              question: schemaField.field_name,
              reasoning: currentNotes || 'Added by user interaction'
            });
          }
        });
      }
      
      return {
        ...categoryResult,
        field_answers: updatedFieldAnswers
      };
    });

    const updatedResult: WebsiteExtractionResult = {
      ...result,
      categories: updatedCategories,
      updated_at: new Date().toISOString()
    };

    setResult(updatedResult);
    console.log('Saved result:', updatedResult);
    toast.success('Progress saved successfully');
  };

  const goToNext = () => {
    // Generate instructions for current category before moving
    generateCategoryInstructions(currentCategory.category_id);
    setCurrentCategoryIdx((idx) => Math.min(idx + 1, totalCategories - 1));
  };
  
  const goToPrev = () => {
    // Generate instructions for current category before moving
    generateCategoryInstructions(currentCategory.category_id);
    setCurrentCategoryIdx((idx) => Math.max(idx - 1, 0));
  };

  return (
    <div className="min-h-screen bg-kay-slate flex flex-col">
      {/* Header */}
      <HeaderSection
        currentCategoryIdx={currentCategoryIdx}
        currentCategoryName={currentCategory.category_name}
        hasUnsavedChanges={hasUnsavedChanges}
        onRefresh={handleRefresh}
        onSave={saveToResult}
        progressPercent={progressPercent}
        totalCategories={totalCategories}
        websiteName={websiteSchema.website_name}
      />
      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row gap-5 max-w-6xl mx-auto w-full py-8">
        {/* Left Card (Fields) */}
        <div className="flex-1">
          <div className="bg-white rounded-xl shadow p-0 overflow-hidden">
            {/* Section Title */}
            <div className="bg-gradient-to-r from-kay-main to-kay text-kay-black px-6 py-3 text-base font-semibold flex items-center justify-between">
              <span>Fill in the Information</span>
              <span className="bg-kay/20 text-kay text-xs font-semibold px-3 py-1 rounded-lg shadow-sm whitespace-nowrap">
                {fieldsCompleted} / {fields.length} completed
              </span>
            </div>
            <div className="p-6 flex flex-col justify-between min-h-[55vh] gap-4">
              <div className="flex flex-col justify-between gap-2 max-h-[520px] overflow-y-auto pr-2">
                {fields.map((field: WebsiteField) => (
                  <SingleFieldEditor
                    categoryId={currentCategory.category_id}
                    field={field}
                    fieldStatus={fieldStatuses[currentCategory.category_id]?.[field.field_id] || FieldStatus.Pending}
                    isAvailable={isFieldAvailable[currentCategory.category_id]?.[field.field_id] || false}
                    key={field.field_id}
                    notes={notes[currentCategory.category_id]?.[field.field_id] || ''}
                    onChange={handleFieldChange}
                    onNotesChange={handleNotesChange}
                    onStatusChange={handleFieldStatusChange}
                    reasoning={reasoning[currentCategory.category_id]?.[field.field_id]}
                    value={answers[currentCategory.category_id]?.[field.field_id] || ''}
                  />
                ))}
              </div>

            </div>
          </div>
        </div>
        {/* Right Card (Reference/Screenshot) */}
        <div className="w-full flex md:w-[350px] flex-shrink-0">
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {/* Green header */}
            <div className="bg-gradient-to-r from-kay-main to-kay text-kay-black px-6 py-3 text-base font-semibold flex items-center justify-between">
              <span>{currentCategory.category_name}</span>
              <span className={`text-xs font-semibold px-2 py-1 rounded ${getCategoryStatusColor(categoryStatuses[currentCategory.category_id] || CategoryStatus.Pending)}`}>
                {getCategoryStatusText(categoryStatuses[currentCategory.category_id] || CategoryStatus.Pending)}
              </span>
            </div>
            <div className="p-4 flex flex-col gap-2">
              {/* Info box */}
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded p-3 text-blue-800 text-sm">
                {currentCategory?.category_description}
              </div>
              
              {/* Category Instructions */}
              <div className="mb-1">
                <Label className="text-sm font-semibold text-gray-800 block">
                  Category Instructions
                </Label>
                <Textarea
                  className="min-h-[300px] text-sm border border-gray-300 bg-gray-50 resize-none"
                  placeholder="Instructions will be auto-generated from completed field notes..."
                  readOnly
                  value={categoryInstructions[currentCategory.category_id] || ''}
                />
                <p className="text-xs text-gray-500 mt-1">
                  ℹ️ Auto-generated from completed field instructions
                </p>
              </div>
              
              <div className="font-semibold text-gray-800 mb-2">Reference Screenshots</div>
              {/* Category Screenshots Grid or Fallback */}
              {/* Simpler and readable screenshots prop */}
              {/* <CategoryScreenshots screenshots={currentCategoryWithScreenshots.screenshots ?? []} /> */}

              <div>
                <div>
                  Documents
                </div>
                <div>
                  Category Screenshots
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
      {/* Footer Navigation */}
      <FinishActions
        currentCategoryIdx={currentCategoryIdx}
        onCopyAnswers={handleCopyAnswers}
        onDownload={handleDownload}
        onFinish={() => {
          generateCategoryInstructions(currentCategory.category_id);
          setCurrentCategoryIdx(totalCategories - 1);
        }}
        onNext={goToNext}
        onPrevious={goToPrev}
        totalCategories={totalCategories}
      />
    </div>
  );
};

export default WebsiteSchemaEditor;
